export class NativeValidationService {
    private static instance: NativeValidationService;
    private validatedForms = new WeakSet<HTMLFormElement>();

    private constructor() {}

    static getInstance(): NativeValidationService {
        if (!NativeValidationService.instance) {
            NativeValidationService.instance = new NativeValidationService();
        }
        return NativeValidationService.instance;
    }

    /**
     * Parse and initialize validation for a form
     * @param form The form element to validate
     */
    parse(form: HTMLFormElement): void {
        if (this.validatedForms.has(form)) {
            return;
        }

        this.setupValidation(form);
        this.validatedForms.add(form);
    }

    /**
     * Validate a form manually
     * @param form The form to validate
     * @returns True if valid, false otherwise
     */
    validate(form: HTMLFormElement): boolean {
        const inputs = form.querySelectorAll('input, select, textarea');
        let isValid = true;

        inputs.forEach(input => {
            if (!this.validateField(input as HTMLInputElement)) {
                isValid = false;
            }
        });

        return isValid;
    }

    /**
     * Reset validation state for a form
     * @param form The form to reset
     */
    resetValidation(form: HTMLFormElement): void {
        const errorElements = form.querySelectorAll('.field-validation-error, .validation-summary-errors');
        errorElements.forEach(element => {
            element.textContent = '';
            element.classList.remove('field-validation-error');
            element.classList.add('field-validation-valid');
        });

        const invalidInputs = form.querySelectorAll('.input-validation-error');
        invalidInputs.forEach(input => {
            input.classList.remove('input-validation-error');
            input.classList.add('valid');
        });
    }

    private setupValidation(form: HTMLFormElement): void {
        // Setup real-time validation
        const inputs = form.querySelectorAll('input, select, textarea');
        
        inputs.forEach(input => {
            const inputElement = input as HTMLInputElement;
            
            // Validate on blur for better UX
            inputElement.addEventListener('blur', () => {
                this.validateField(inputElement);
            });

            // Clear errors on input
            inputElement.addEventListener('input', () => {
                this.clearFieldError(inputElement);
            });
        });

        // Validate on form submission
        form.addEventListener('submit', (e) => {
            if (!this.validate(form)) {
                e.preventDefault();
                e.stopPropagation();
                
                // Focus first invalid field
                const firstInvalid = form.querySelector('.input-validation-error') as HTMLInputElement;
                if (firstInvalid) {
                    firstInvalid.focus();
                }
            }
        });
    }

    private validateField(input: HTMLInputElement): boolean {
        const rules = this.getValidationRules(input);
        let isValid = true;
        let errorMessage = '';

        // Check HTML5 validity first
        if (!input.checkValidity()) {
            isValid = false;
            errorMessage = input.validationMessage || 'This field is invalid.';
        }

        // Check custom validation rules
        for (const rule of rules) {
            if (!this.applyRule(input, rule)) {
                isValid = false;
                errorMessage = rule.message || 'This field is invalid.';
                break;
            }
        }

        this.updateFieldValidationState(input, isValid, errorMessage);
        return isValid;
    }

    private getValidationRules(input: HTMLInputElement): ValidationRule[] {
        const rules: ValidationRule[] = [];

        // Extract rules from data attributes (ASP.NET style)
        if (input.hasAttribute('data-val')) {
            if (input.hasAttribute('data-val-required')) {
                rules.push({
                    type: 'required',
                    message: input.getAttribute('data-val-required') || 'This field is required.'
                });
            }

            if (input.hasAttribute('data-val-email')) {
                rules.push({
                    type: 'email',
                    message: input.getAttribute('data-val-email') || 'Please enter a valid email address.'
                });
            }

            if (input.hasAttribute('data-val-length')) {
                const min = input.getAttribute('data-val-length-min');
                const max = input.getAttribute('data-val-length-max');
                rules.push({
                    type: 'length',
                    message: input.getAttribute('data-val-length') || 'Field length is invalid.',
                    min: min ? parseInt(min) : undefined,
                    max: max ? parseInt(max) : undefined
                });
            }

            if (input.hasAttribute('data-val-range')) {
                const min = input.getAttribute('data-val-range-min');
                const max = input.getAttribute('data-val-range-max');
                rules.push({
                    type: 'range',
                    message: input.getAttribute('data-val-range') || 'Value is out of range.',
                    min: min ? parseFloat(min) : undefined,
                    max: max ? parseFloat(max) : undefined
                });
            }

            if (input.hasAttribute('data-val-regex')) {
                const pattern = input.getAttribute('data-val-regex-pattern');
                if (pattern) {
                    rules.push({
                        type: 'regex',
                        message: input.getAttribute('data-val-regex') || 'Field format is invalid.',
                        pattern: new RegExp(pattern)
                    });
                }
            }
        }

        return rules;
    }

    private applyRule(input: HTMLInputElement, rule: ValidationRule): boolean {
        const value = input.value.trim();

        switch (rule.type) {
            case 'required':
                // A checkbox/radio carries a fixed `value` attribute (value="true" on our
                // hand-rolled required checkboxes) whether or not it is selected, so the generic
                // `value.length > 0` test below is always satisfied for one and the rule silently
                // passes. That is why an unticked AgreeToTerms on the account creation form —
                // a form.g-recaptcha, so recaptcha-service.ts routes its submit through this
                // service rather than aspnet-client-validation — produced no error at all. For
                // these inputs "has a value" means `checked`, as aspnet-client-validation treats it.
                if (input.type === 'checkbox' || input.type === 'radio') {
                    return this.isSelectionMade(input);
                }
                return value.length > 0;

            case 'email':
                if (!value) return true; // Optional field
                const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
                return emailRegex.test(value);

            case 'length':
                if (!value) return true; // Optional field
                const length = value.length;
                if (rule.min !== undefined && length < rule.min) return false;
                if (rule.max !== undefined && length > rule.max) return false;
                return true;

            case 'range':
                if (!value) return true; // Optional field
                const numValue = parseFloat(value);
                if (isNaN(numValue)) return false;
                if (rule.min !== undefined && numValue < rule.min) return false;
                if (rule.max !== undefined && numValue > rule.max) return false;
                return true;

            case 'regex':
                if (!value) return true; // Optional field
                return rule.pattern?.test(value) ?? true;

            default:
                return true;
        }
    }

    /**
     * Whether a required checkbox/radio has been selected. Grouped inputs (radio groups, and
     * checkbox lists sharing one name) are satisfied by any single member being checked, so the
     * whole group is considered — scoped to the input's own form, never the document, since field
     * names are not unique across a page (see findValidationSpan for the same reasoning).
     */
    private isSelectionMade(input: HTMLInputElement): boolean {
        const scope = input.form;

        if (!scope || !input.name) {
            return input.checked;
        }

        const escapedName = CSS.escape(input.name);

        // A checkbox rendered by @Html.CheckBoxFor gets a hidden companion of the same name with
        // value="false", so a value is posted even when it is unticked. That matters here because
        // MVC auto-emits data-val-required on any non-nullable bool — so an entirely *optional*
        // preference (ContactMeByEmail on the account creation form) looks required to us, and
        // would block submission with no message at all, since CheckBoxFor fields have no
        // data-valmsg-for span to render one into. The companion guarantees a posted value, so
        // treat required as satisfied; aspnet-client-validation makes exactly the same allowance.
        // Genuinely required checkboxes are hand-rolled *without* a companion precisely so this
        // does not apply to them — see rule 4 in .github/instructions/validation.md.
        if (input.type === 'checkbox') {
            const companion = scope.querySelector<HTMLInputElement>(
                `input[type="hidden"][name="${escapedName}"]`);

            if (companion?.value === 'false') {
                return true;
            }
        }

        const group = scope.querySelectorAll<HTMLInputElement>(
            `input[type="${input.type}"][name="${escapedName}"]`);

        return Array.from(group).some(member => member.checked);
    }

    private updateFieldValidationState(input: HTMLInputElement, isValid: boolean, errorMessage: string): void {
        // Update input classes
        input.classList.remove('input-validation-error', 'valid');
        input.classList.add(isValid ? 'valid' : 'input-validation-error');

        // Find validation span
        const validationSpan = this.findValidationSpan(input);
        if (validationSpan) {
            validationSpan.textContent = isValid ? '' : errorMessage;
            validationSpan.classList.remove('field-validation-error', 'field-validation-valid');
            validationSpan.classList.add(isValid ? 'field-validation-valid' : 'field-validation-error');
        }
    }

    private clearFieldError(input: HTMLInputElement): void {
        input.classList.remove('input-validation-error');
        
        const validationSpan = this.findValidationSpan(input);
        if (validationSpan && validationSpan.classList.contains('field-validation-error')) {
            validationSpan.textContent = '';
            validationSpan.classList.remove('field-validation-error');
            validationSpan.classList.add('field-validation-valid');
        }
    }

    private findValidationSpan(input: HTMLInputElement): HTMLElement | null {
        // Look for validation span using common patterns
        const inputId = input.id || input.name;
        if (inputId) {
            // Scoped to the input's own form, not the whole document: field names/ids like
            // FirstName/LastName/Email are common enough that more than one form on the same
            // page can share them (e.g. the sitewide donation popup vs. an account form). An
            // unscoped document-wide lookup silently resolves to whichever form happens to come
            // first in the DOM, writing the error message into a span the user never sees while
            // the field they're actually looking at stays blank. aspnet-client-validation avoids
            // this by scoping every lookup to element.form; do the same here.
            const scope = input.form ?? document;

            // Try standard ASP.NET pattern
            let span = scope.querySelector(`[data-valmsg-for="${inputId}"]`) as HTMLElement;
            if (span) return span;

            // Try by ID
            span = scope.querySelector(`#${inputId}-error`) as HTMLElement;
            if (span) return span;
        }

        // Look for sibling span with validation classes
        const sibling = input.nextElementSibling;
        if (sibling && (sibling.classList.contains('field-validation-error') || 
                       sibling.classList.contains('field-validation-valid'))) {
            return sibling as HTMLElement;
        }

        return null;
    }
}

interface ValidationRule {
    type: 'required' | 'email' | 'length' | 'range' | 'regex';
    message: string;
    min?: number;
    max?: number;
    pattern?: RegExp;
}
